/**
 * The lights out rules, ported from `apps/puzzletypes/lights_out/board.py`. The two are kept
 * at parity by the shared fixtures in `shared/fixtures/lights_out/`.
 *
 * Pure: it reads a pattern and presses and returns positions. A position is the set of lit
 * cells, and a press is a symmetric difference — which is why the order of the presses never
 * changes the outcome, and why solvability is a linear system over GF(2).
 */
import type { Press } from "./types";

const NEIGHBOURHOOD: Press[] = [
  [0, 0],
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

/** Cells live in sets, so they travel as `row,col` keys. */
export function key(row: number, col: number): string {
  return `${row},${col}`;
}

export function parseLights(rows: string[]): Set<string> {
  const lit = new Set<string>();
  rows.forEach((row, r) =>
    Array.from(row).forEach((char, c) => {
      if (char === "1") lit.add(key(r, c));
    }),
  );
  return lit;
}

export function render(size: number, lit: Set<string>): string[] {
  return Array.from({ length: size }, (_, r) =>
    Array.from({ length: size }, (_, c) => (lit.has(key(r, c)) ? "1" : "0")).join(""),
  );
}

export function inside(size: number, [r, c]: Press): boolean {
  return r >= 0 && c >= 0 && r < size && c < size;
}

/** The cells one press flips: itself and whichever neighbours exist. */
export function toggled(size: number, [r, c]: Press): string[] {
  return NEIGHBOURHOOD.map(([dr, dc]) => [r + dr, c + dc] as Press)
    .filter((cell) => inside(size, cell))
    .map(([row, col]) => key(row, col));
}

export function press(size: number, lit: Set<string>, cell: Press): Set<string> {
  const next = new Set(lit);
  for (const id of toggled(size, cell)) {
    if (next.has(id)) next.delete(id);
    else next.add(id);
  }
  return next;
}

/** A `[row, col]` pair of integers, or `null` for anything else. */
export function asPress(raw: unknown): Press | null {
  if (!Array.isArray(raw) || raw.length !== 2) return null;
  const [r, c] = raw as unknown[];
  if (!Number.isInteger(r) || !Number.isInteger(c)) return null;
  return [r as number, c as number];
}

export interface Replay {
  lit: Set<string>;
  /** The presses played, up to the first illegal one. */
  presses: Press[];
  /** Index of the first press that is not a cell of the board, or `null`. */
  illegalAt: number | null;
}

/** Play a press list from `start`, stopping at the first press off the board. */
export function replay(size: number, start: Set<string>, presses: unknown[]): Replay {
  let lit = start;
  const played: Press[] = [];
  for (let index = 0; index < presses.length; index += 1) {
    const cell = asPress(presses[index]);
    if (!cell || !inside(size, cell)) return { lit, presses: played, illegalAt: index };
    lit = press(size, lit, cell);
    played.push(cell);
  }
  return { lit, presses: played, illegalAt: null };
}

export function isSolved(lit: Set<string>): boolean {
  return lit.size === 0;
}

/**
 * One set of presses that turns every light off, or `null` when there is none. Gaussian
 * elimination over GF(2), rows as arrays of bits with the right-hand side last; free
 * variables are left unpressed. Used to refuse an unsolvable pattern, never to judge.
 */
export function solve(size: number, lit: Set<string>): Press[] | null {
  const count = size * size;
  const rows: number[][] = [];
  for (let r = 0; r < size; r += 1) {
    for (let c = 0; c < size; c += 1) {
      const row = new Array<number>(count + 1).fill(0);
      for (const id of toggled(size, [r, c])) {
        const [tr, tc] = id.split(",").map(Number);
        row[tr * size + tc] = 1;
      }
      row[count] = lit.has(key(r, c)) ? 1 : 0;
      rows.push(row);
    }
  }
  const pivots: number[] = [];
  let rank = 0;
  for (let col = 0; col < count; col += 1) {
    let pivot = -1;
    for (let k = rank; k < count; k += 1)
      if (rows[k][col] === 1) {
        pivot = k;
        break;
      }
    if (pivot < 0) continue;
    [rows[rank], rows[pivot]] = [rows[pivot], rows[rank]];
    for (let k = 0; k < count; k += 1) {
      if (k === rank || rows[k][col] === 0) continue;
      for (let j = col; j <= count; j += 1) rows[k][j] ^= rows[rank][j];
    }
    pivots.push(col);
    rank += 1;
  }
  for (let k = rank; k < count; k += 1) if (rows[k][count] === 1) return null;
  const chosen: Press[] = [];
  pivots.forEach((col, k) => {
    if (rows[k][count] === 1) chosen.push([Math.floor(col / size), col % size]);
  });
  return chosen.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
}

export function isSolvable(size: number, lit: Set<string>): boolean {
  return solve(size, lit) !== null;
}
